import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PackageX } from 'lucide-react';
import useTenantFeatures from '../hooks/useTenantFeatures';

// Route guard for a PER-TENANT feature — what the hospital bought.
//
// Not to be confused with FeatureGuard, which gates on a DOCTOR's subscription
// plan (config/accessControls.js) and passes any non-doctor straight through.
// Using that one here would be a silent no-op for the admins these pages are
// built for: it would look like a gate and gate nothing.
//
// The sidebar already hides entries for features a hospital hasn't got, but a
// bookmark or a pasted link skips the sidebar entirely — and without this the
// page renders and then every call on it returns FEATURE_DISABLED, which reads
// as a broken screen rather than one that isn't part of the plan.
//
// FAILS OPEN, deliberately. useTenantFeatures returns allow-all when the lookup
// fails, so a dropped request shows the page rather than accusing a paying
// hospital of not having bought something. The API enforces the real rule via
// requireTenantFeature, so the worst case is a page that cannot load its data.
export default function TenantFeatureRoute({ feature, children }) {
  const navigate = useNavigate();
  const { has } = useTenantFeatures();
  // Through t(), so it reads in French too; the tenant-kind words ("hospital"
  // -> clinic / the tenant's name) come from i18n's orgWords post-processor.
  const { t } = useTranslation();

  if (has(feature)) return children || <Outlet />;

  return (
    <div className="w-full flex items-center justify-center py-16 px-4" data-testid={`tenant-feature-locked-${feature}`}>
      <div className="bg-white rounded-2xl shadow-md max-w-md w-full p-8 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center">
          <PackageX className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold text-[#1a365d] mb-2">{t('featureLock.tenant_title')}</h2>
        <p className="text-sm text-slate-500 mb-6">{t('featureLock.tenant_message')}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          data-testid={`tenant-feature-locked-cta-${feature}`}
          className="w-full px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg"
        >
          {t('featureLock.tenant_cta')}
        </button>
      </div>
    </div>
  );
}
